import { Flame, Moon, Heart, Brain, Zap, Scale, Droplet, Check } from 'lucide-react';

interface Goal {
  value: string;
  label: string;
  icon: typeof Flame;
  description: string;
}

const WOMENS_GOALS: Goal[] = [
  { 
    value: 'vasomotor', 
    label: 'Reduce hot flashes/night sweats',
    icon: Flame,
    description: 'Manage temperature regulation symptoms',
  },
  { 
    value: 'sleep', 
    label: 'Improve sleep quality',
    icon: Moon,
    description: 'Better rest and recovery',
  },
  { 
    value: 'libido', 
    label: 'Boost libido/sexual wellness',
    icon: Heart,
    description: 'Enhance intimacy and desire',
  },
  { 
    value: 'mood', 
    label: 'Stabilize mood/anxiety',
    icon: Brain,
    description: 'Emotional balance and wellbeing',
  },
  { 
    value: 'energy', 
    label: 'Enhance energy & focus',
    icon: Zap,
    description: 'Boost vitality and mental clarity',
  },
  { 
    value: 'metabolic', 
    label: 'Metabolic/weight support',
    icon: Scale,
    description: 'Optimize metabolism and body composition',
  },
  { 
    value: 'vaginal_comfort', 
    label: 'Vaginal comfort',
    icon: Droplet,
    description: 'Improve comfort and moisture',
  },
];

interface GoalsStepProps {
  selectedGoals: string[];
  onGoalsChange: (goals: string[]) => void;
}

export default function GoalsStep({ selectedGoals, onGoalsChange }: GoalsStepProps) {
  const toggleGoal = (goalValue: string) => {
    if (selectedGoals.includes(goalValue)) {
      onGoalsChange(selectedGoals.filter(g => g !== goalValue));
    } else if (selectedGoals.length < 3) {
      onGoalsChange([...selectedGoals, goalValue]);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-3">
        <p className="text-sm font-medium uppercase tracking-wider" style={{ color: '#2FFFC7' }}>
          Step 1 of 7
        </p>
        <h2 className="text-3xl font-light tracking-tight" style={{ color: '#E6E9F0' }}>
          What are your top health goals?
        </h2>
        <p className="text-lg" style={{ color: '#C1C9D2' }}>
          Select up to 3 goals you'd like to prioritize
        </p>
      </div>

      {/* Goal Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {WOMENS_GOALS.map((goal) => {
          const Icon = goal.icon;
          const isSelected = selectedGoals.includes(goal.value);
          const isDisabled = selectedGoals.length >= 3 && !isSelected;
          const selectionNumber = selectedGoals.indexOf(goal.value) + 1;
          
          return (
            <button
              key={goal.value}
              type="button"
              onClick={() => toggleGoal(goal.value)}
              disabled={isDisabled}
              className="relative p-6 rounded-xl text-left transition-all duration-200 hover:scale-[1.02]"
              style={{
                background: isSelected 
                  ? 'rgba(47, 255, 199, 0.1)' 
                  : 'rgba(255, 255, 255, 0.04)',
                border: isSelected 
                  ? '2px solid #2FFFC7' 
                  : '1px solid rgba(255, 255, 255, 0.08)',
                opacity: isDisabled ? 0.5 : 1,
                cursor: isDisabled ? 'not-allowed' : 'pointer',
              }}
            >
              {/* Content */}
              <div className="flex items-start space-x-4">
                <div 
                  className="p-3 rounded-lg"
                  style={{
                    background: isSelected 
                      ? 'rgba(47, 255, 199, 0.2)' 
                      : 'rgba(255, 255, 255, 0.05)',
                  }}
                >
                  <Icon 
                    className="h-6 w-6" 
                    style={{ color: isSelected ? '#2FFFC7' : '#9DA9B8' }}
                  />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium mb-1" style={{ color: '#E6E9F0' }}>
                    {goal.label}
                  </h3>
                  <p className="text-sm" style={{ color: '#9DA9B8' }}>
                    {goal.description}
                  </p>
                </div>
                
                {/* Selection Number Badge */}
                {isSelected && (
                  <div
                    className="absolute top-4 right-4 h-8 w-8 rounded-full flex items-center justify-center"
                    style={{
                      background: 'linear-gradient(90deg, #2FFFC7, #3B82F6)',
                      boxShadow: '0 4px 12px rgba(47, 255, 199, 0.3)',
                    }}
                  >
                    <span className="text-sm font-semibold text-black">
                      {selectionNumber}
                    </span>
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Selected Goals Summary */}
      {selectedGoals.length > 0 && (
        <div
          className="p-6 rounded-xl"
          style={{
            background: 'rgba(47, 255, 199, 0.05)',
            border: '1px solid rgba(47, 255, 199, 0.2)',
          }}
        >
          <div className="flex items-center gap-2 mb-3">
            <Check className="h-5 w-5" style={{ color: '#2FFFC7' }} />
            <p className="font-medium" style={{ color: '#E6E9F0' }}>
              Selected Goals ({selectedGoals.length}/3)
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {selectedGoals.map(goalValue => {
              const goal = WOMENS_GOALS.find(g => g.value === goalValue);
              const number = selectedGoals.indexOf(goalValue) + 1;
              return (
                <span
                  key={goalValue}
                  className="px-3 py-1 rounded-full text-sm font-medium"
                  style={{
                    background: 'rgba(47, 255, 199, 0.2)',
                    color: '#2FFFC7',
                  }}
                >
                  {number}. {goal?.label}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Validation Error */}
      {selectedGoals.length === 0 && (
        <p className="text-sm" style={{ color: '#9DA9B8' }}>
          Please select at least one goal to continue
        </p>
      )}
    </div>
  );
}

export { WOMENS_GOALS };
